import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, BellRing, LogOut, RefreshCw, Lock, User, Phone, MapPin, Mail, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { ASSETS, waLink } from '../lib/products';
import KrispButton from '../components/KrispButton';

const STATUSES = ['PLACED', 'CONFIRMED', 'SHIPPED', 'DELIVERED', 'CANCELLED'];
const STATUS_COLOR = { PLACED: '#F7B500', CONFIRMED: '#3E9B4F', SHIPPED: '#E0632A', DELIVERED: '#2A1010', CANCELLED: '#E11D24' };
const POLL_MS = 20000;

const auth = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const IS_IOS = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/.test(navigator.userAgent);
const IS_STANDALONE = typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);
const PUSH_SUPPORTED = typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

const toKey = (b64) => {
  const pad = '='.repeat((4 - (b64.length % 4)) % 4);
  const raw = atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

const subscribePush = async (token) => {
  const reg = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { data } = await api.get('/push/key', auth(token));
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(data.publicKey) });
  }
  const { data } = await api.post('/push/subscribe', { ...sub.toJSON(), device: navigator.userAgent }, auth(token));
  return data.devices;
};

const showNotification = async (order) => {
  const title = `NEW ORDER #${order.id} · ₹${order.total}`;
  const body = `${order.customer.name} · ${order.items.map((i) => `${i.name} ×${i.qty}`).join(', ')}`;
  try {
    const reg = await navigator.serviceWorker?.ready;
    if (reg?.showNotification) { await reg.showNotification(title, { body, icon: '/favicon-v2.png', badge: '/favicon-v2.png', tag: order.id, vibrate: [200, 100, 200] }); return null; }
  } catch { /* fall through */ }
  try { if ('Notification' in window) new Notification(title, { body, icon: '/favicon-v2.png' }); } catch { /* ignore */ }
  return null;
};

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem('pk_admin_token') || '');
  const [creds, setCreds] = useState({ username: '', password: '' });
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState({ count: 0, revenue: 0 });
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState(() => (typeof Notification !== 'undefined' && Notification.permission === 'granted' && localStorage.getItem('pk_alerts') === 'on'));
  const [pushOn, setPushOn] = useState(() => localStorage.getItem('pk_push') === 'on');
  const [devices, setDevices] = useState(null);
  const latest = useRef(null);

  const logout = () => { localStorage.removeItem('pk_admin_token'); setToken(''); setOrders([]); };

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await api.get('/admin/orders', auth(token));
      setOrders(data.orders); setMeta({ count: data.count, revenue: data.revenue });
      latest.current = data.orders[0]?.created_at || null;
    } catch (e) {
      if (e?.response?.status === 401) { logout(); toast('SESSION EXPIRED — LOG IN AGAIN'); }
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!token || !alerts) return undefined;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get('/admin/orders', { ...auth(token), params: latest.current ? { since: latest.current } : {} });
        if (data.orders.length) {
          if (!pushOn) data.orders.forEach(showNotification);
          toast(`${data.orders.length} NEW ORDER${data.orders.length > 1 ? 'S' : ''} 🔔`);
          load();
        }
      } catch { /* retry next tick */ }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [token, alerts, pushOn, load]);

  useEffect(() => {
    if (!token) return;
    api.get('/push/devices', auth(token)).then(({ data }) => setDevices(data.devices)).catch(() => {});
    if (!PUSH_SUPPORTED || localStorage.getItem('pk_push') !== 'on') return;
    navigator.serviceWorker.getRegistration('/').then((reg) => reg?.pushManager.getSubscription()).then((sub) => {
      if (sub && Notification.permission === 'granted') return subscribePush(token).then(setDevices);
      localStorage.removeItem('pk_push'); setPushOn(false);
      return null;
    }).catch(() => {});
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post('/admin/login', creds);
      localStorage.setItem('pk_admin_token', data.token); setToken(data.token);
      toast('WELCOME BACK, PROKRISP.');
    } catch { toast('WRONG USERNAME OR PASSWORD'); }
  };

  const enableAlerts = async () => {
    if (!('Notification' in window)) { toast('THIS BROWSER DOES NOT SUPPORT NOTIFICATIONS'); return; }
    if (IS_IOS && !IS_STANDALONE) { toast('ON IPHONE: TAP SHARE → "ADD TO HOME SCREEN", THEN OPEN THE PROKRISP APP AND TAP THIS BUTTON'); return; }
    const perm = await Notification.requestPermission();
    if (perm !== 'granted') { toast('NOTIFICATIONS BLOCKED — ALLOW THEM IN BROWSER SETTINGS'); return; }
    localStorage.setItem('pk_alerts', 'on'); setAlerts(true);
    if (PUSH_SUPPORTED) {
      try {
        const n = await subscribePush(token);
        localStorage.setItem('pk_push', 'on'); setPushOn(true); setDevices(n);
        await api.post('/push/test', {}, auth(token));
        toast('ORDER ALERTS ON — YOU WILL BE PINGED EVEN WHEN THIS SITE IS CLOSED');
        return;
      } catch (e) { console.warn('push subscribe failed', e); }
    }
    showNotification({ id: 'TEST', total: 0, customer: { name: 'Order alerts are ON' }, items: [{ name: 'You will be notified here', qty: 1 }] });
    toast('ORDER ALERTS ON — KEEP THIS TAB OPEN (BACKGROUND PUSH NOT SUPPORTED HERE)');
  };

  const sendTest = async () => {
    try { const { data } = await api.post('/push/test', {}, auth(token)); toast(`TEST ALERT SENT TO ${data.sent} DEVICE${data.sent === 1 ? '' : 'S'}`); }
    catch { toast('COULD NOT SEND TEST ALERT'); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}/status`, { status }, auth(token));
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, status } : o)));
    } catch { toast('COULD NOT UPDATE STATUS'); }
  };

  if (!token) {
    return (
      <main data-testid="admin-login-page" className="min-h-screen bg-ink text-cream flex items-center justify-center px-5">
        <form onSubmit={login} className="w-full max-w-sm rounded-[2rem] bg-cocoa border-2 border-cocoa p-8 shadow-[10px_10px_0_#E11D24]">
          <img src={ASSETS.logo} alt="PROKRISP" className="h-10 object-contain" />
          <p className="font-display text-4xl mt-5 leading-none">BACKEND LOGIN</p>
          <p className="font-cond text-[10px] tracking-[0.3em] text-cream/60 mt-1 flex items-center gap-2"><Lock size={11} /> ORDERS DASHBOARD</p>
          <label className="block mt-6"><span className="font-cond text-[10px] tracking-[0.3em] text-cream/60">USERNAME</span>
            <input data-testid="admin-username" className="k-input mt-1.5 !rounded-2xl" value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} autoComplete="username" /></label>
          <label className="block mt-3"><span className="font-cond text-[10px] tracking-[0.3em] text-cream/60">PASSWORD</span>
            <input data-testid="admin-password" type="password" className="k-input mt-1.5 !rounded-2xl" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} autoComplete="current-password" /></label>
          <KrispButton testid="admin-login-submit" type="submit" className="w-full mt-6">LOG IN →</KrispButton>
        </form>
      </main>
    );
  }

  return (
    <main data-testid="admin-page" className="min-h-screen bg-cream">
      <header className="bg-ink text-cream px-4 sm:px-8 py-4 flex items-center justify-between gap-3 sticky top-0 z-50 shadow-[0_4px_0_#E11D24]">
        <div className="flex items-center gap-3"><img src={ASSETS.logo} alt="PROKRISP" className="h-7 object-contain" /><span className="font-cond text-[10px] tracking-[0.3em] text-kyellow hidden sm:inline">BACKEND</span></div>
        <div className="flex items-center gap-2">
          <button data-testid="admin-refresh" onClick={load} aria-label="Refresh" className={`w-10 h-10 rounded-full border-2 border-cream/30 flex items-center justify-center hover:bg-cream hover:text-cocoa transition-colors ${loading ? 'animate-spin' : ''}`}><RefreshCw size={16} /></button>
          <button data-testid="admin-logout" onClick={logout} aria-label="Log out" className="w-10 h-10 rounded-full border-2 border-cream/30 flex items-center justify-center hover:bg-cream hover:text-cocoa transition-colors"><LogOut size={16} /></button>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="rounded-2xl bg-cocoa text-cream p-4"><p className="font-cond text-[10px] tracking-[0.3em] text-kyellow">ORDERS</p><p data-testid="admin-order-count" className="font-display text-5xl leading-none mt-1">{meta.count}</p></div>
          <div className="rounded-2xl bg-kyellow text-cocoa p-4"><p className="font-cond text-[10px] tracking-[0.3em]">REVENUE</p><p data-testid="admin-revenue" className="font-display text-5xl leading-none mt-1">₹{meta.revenue ?? 0}</p></div>
          <button data-testid="admin-alerts-button" onClick={enableAlerts}
            className={`col-span-2 sm:col-span-1 rounded-2xl border-2 border-cocoa p-4 text-left transition-colors ${alerts ? 'bg-[#3E9B4F] text-cream' : 'bg-white text-cocoa hover:bg-butter'}`}>
            <p className="font-cond text-[10px] tracking-[0.3em] flex items-center gap-2">{alerts ? <BellRing size={12} /> : <Bell size={12} />} {alerts ? (pushOn ? 'ALERTS ON · THIS DEVICE' : 'ALERTS ON · TAB ONLY') : 'NOTIFICATIONS'}</p>
            <p className="font-display text-2xl leading-none mt-1">GET ORDER ALERTS</p>
            <p data-testid="admin-alerts-hint" className="text-[10px] mt-1 opacity-80">
              {alerts ? (pushOn ? `Pings arrive even when the site is closed${devices === 0 ? ' · no device linked yet, tap again' : devices ? ` · ${devices} device${devices === 1 ? '' : 's'} linked` : ''}.` : 'Keep this page open on your phone to be pinged.')
                : (IS_IOS && !IS_STANDALONE ? 'iPhone: Share → Add to Home Screen first, then tap here.' : 'Tap to allow notifications on this device.')}
            </p>
          </button>
        </div>
        {alerts && pushOn && (
          <div className="flex flex-wrap items-center gap-3 mt-3 font-cond text-[10px] tracking-[0.25em] text-cocoa/70">
            <button data-testid="admin-test-alert" onClick={sendTest} className="underline hover:text-kred">SEND TEST ALERT</button>
            <span>·</span>
            <span>Repeat "GET ORDER ALERTS" on each phone/laptop you want pinged.</span>
          </div>
        )}

        <div className="mt-6 space-y-3" data-testid="admin-orders-list">
          {orders.length === 0 && <p className="text-center font-cond tracking-[0.3em] text-cocoa/50 py-16">NO ORDERS YET</p>}
          {orders.map((o) => (
            <div key={o.id} data-testid={`admin-order-${o.id}`} className="rounded-2xl bg-white/80 border-2 border-cocoa p-4 sm:p-5">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-display text-3xl leading-none text-cocoa">#{o.id} <span className="text-kred">₹{o.total}</span></p>
                  <p className="font-cond text-[10px] tracking-[0.25em] text-cocoa/60 mt-1">{o.created_at?.slice(0, 16).replace('T', ' ')} UTC · {o.items.reduce((a, i) => a + i.qty, 0)} PACKETS · ALERT {o.alert_status || 'PENDING'}</p>
                </div>
                <select data-testid={`admin-status-${o.id}`} value={o.status} onChange={(e) => setStatus(o.id, e.target.value)}
                  className="rounded-full border-2 border-cocoa px-3 py-1.5 font-cond font-bold text-xs tracking-wider text-cream" style={{ background: STATUS_COLOR[o.status] || '#2A1010' }}>
                  {STATUSES.map((s) => <option key={s} value={s} className="text-cocoa bg-cream">{s}</option>)}
                </select>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {o.items.map((i) => <span key={i.slug} className="rounded-full bg-cocoa text-cream px-3 py-1 text-[10px] font-black tracking-wider">{i.name} × {i.qty}</span>)}
              </div>
              <div className="grid sm:grid-cols-2 gap-2 mt-3 text-sm text-cocoa">
                <p className="flex items-center gap-2"><User size={14} className="text-cocoa/50" /> {o.customer.name}</p>
                <a href={`tel:${o.customer.phone}`} className="flex items-center gap-2 hover:text-kred"><Phone size={14} className="text-cocoa/50" /> {o.customer.phone}</a>
                <a href={`mailto:${o.customer.email}`} className="flex items-center gap-2 hover:text-kred break-all"><Mail size={14} className="text-cocoa/50" /> {o.customer.email}</a>
                <p className="flex items-start gap-2"><MapPin size={14} className="text-cocoa/50 mt-0.5 shrink-0" /> {o.customer.address}</p>
              </div>
              <a href={waLink(`Hi ${o.customer.name}, this is PROKRISP about your order #${o.id} (₹${o.total}).`).replace('919220888136', o.customer.phone.replace(/\D/g, '').replace(/^(\d{10})$/, '91$1'))} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 mt-3 font-cond font-bold text-xs tracking-wider text-[#3E9B4F] hover:underline"><MessageCircle size={14} /> WHATSAPP CUSTOMER</a>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
